import React, { useState, useEffect } from 'react';
import { api } from '../services/api';

export function ReportsPage() {
  const [reports, setReports] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [generating, setGenerating] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [form, setForm] = useState({
    type: 'weekly',
    from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0],
    userIds: [] as number[],
    title: '',
  });

  useEffect(() => {
    api.get('/reports').then(r => setReports(r.data));
    api.get('/users').then(r => setUsers(r.data));
  }, []);

  const generateReport = async (e: React.FormEvent) => {
    e.preventDefault();
    setGenerating(true);
    setReportData(null);
    try {
      const { data } = await api.post('/reports/generate', {
        ...form,
        title: form.title || `Informe ${form.type} ${form.from} — ${form.to}`,
      });
      setReportData(data);
      setReports(prev => [data, ...prev]);
    } finally {
      setGenerating(false);
    }
  };

  const formatHours = (h: number) => `${h}h`;

  return (
    <div style={{ padding: '28px 32px' }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Informes</h1>
          <p className="page-subtitle">Genera y exporta informes de actividad</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: 20 }}>
        {/* Generator */}
        <div>
          <div className="card">
            <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>📊 Generar informe</h3>
            <form onSubmit={generateReport} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Tipo</label>
                <select className="input" value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
                  <option value="daily">Diario</option>
                  <option value="weekly">Semanal</option>
                  <option value="monthly">Mensual</option>
                  <option value="custom">Personalizado</option>
                  <option value="employee">Por empleado</option>
                  <option value="team">Por equipo</option>
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Desde</label>
                  <input type="date" className="input" value={form.from} onChange={e => setForm({...form, from: e.target.value})} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Hasta</label>
                  <input type="date" className="input" value={form.to} onChange={e => setForm({...form, to: e.target.value})} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Empleados (dejar vacío = todos)</label>
                <select className="input" multiple style={{ height: 100 }}
                  onChange={e => setForm({...form, userIds: Array.from(e.target.selectedOptions, o => +o.value)})}>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <input className="input" placeholder="Título del informe (opcional)" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
              <button type="submit" className="btn btn-primary" disabled={generating}>
                {generating ? '⏳ Generando...' : '📊 Generar informe'}
              </button>
            </form>
          </div>

          {/* History */}
          {reports.length > 0 && (
            <div className="card" style={{ marginTop: 16 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>📋 Historial</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {reports.slice(0, 10).map(r => (
                  <div key={r.id} style={{ padding: '8px 10px', background: 'var(--bg3)', borderRadius: 8, fontSize: 12 }}>
                    <div style={{ fontWeight: 600 }}>{r.title}</div>
                    <div style={{ color: 'var(--text3)', fontSize: 10, marginTop: 2 }}>
                      {r.type} · {new Date(r.createdAt).toLocaleDateString('es-ES')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Report result */}
        <div>
          {reportData ? (
            <div>
              <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>
                  {reportData.title}
                  <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 8 }}>
                    {reportData.params?.from} — {reportData.params?.to}
                  </span>
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
                  {[
                    { label: 'Empleados', value: reportData.data?.totalEmployees || 0 },
                    { label: 'Horas totales', value: `${reportData.data?.totalHours || 0}h` },
                    { label: 'Productividad media', value: `${reportData.data?.avgProductivity || 0}%` },
                  ].map(s => (
                    <div key={s.label} style={{ background: 'var(--bg3)', borderRadius: 10, padding: 14, textAlign: 'center' }}>
                      <div style={{ fontSize: 22, fontWeight: 700 }}>{s.value}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Per-employee table */}
                {reportData.data?.summary?.length > 0 && (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Empleado</th>
                        <th>Horas totales</th>
                        <th>Horas activas</th>
                        <th>Productividad</th>
                        <th>Sesiones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.data.summary.map((u: any) => (
                        <tr key={u.userId}>
                          <td style={{ fontWeight: 600 }}>{u.userName || `#${u.userId}`}</td>
                          <td>{formatHours(u.totalHours)}</td>
                          <td>{formatHours(u.activeHours)}</td>
                          <td>
                            <span className={`badge ${u.productivityPct >= 70 ? 'badge-green' : u.productivityPct >= 40 ? 'badge-accent' : 'badge-red'}`}>
                              {u.productivityPct}%
                            </span>
                          </td>
                          <td>{u.sessionCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Top apps */}
              {reportData.data?.topApps?.length > 0 && (
                <div className="card">
                  <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Top aplicaciones del período</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {reportData.data.topApps.slice(0, 10).map((app: any, i: number) => {
                      const h = Math.floor(app.totalSecs / 3600);
                      const m = Math.floor((app.totalSecs % 3600) / 60);
                      const color = app.productivity === 'productive' ? 'var(--green)' : app.productivity === 'unproductive' ? 'var(--red)' : 'var(--blue)';
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
                          <div style={{ flex: 1, fontSize: 12 }}>{app.appName}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{app.userCount} usuarios</div>
                          <div style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>{h > 0 ? `${h}h ` : ''}{m}m</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="card" style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 48 }}>📊</div>
              <div style={{ color: 'var(--text3)', fontSize: 13 }}>Configura y genera un informe para verlo aquí</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
